import React, { useState, useRef } from "react";
import { Button } from "./ui/Button";
import { Input } from "./ui/Input";
import { Select } from "./ui/Select";
import { parseCSV } from "../utils/csvParser";
import { getAllProfiles, getProfileById } from "../utils/csvPlatforms";
import { apiService } from "../services/api";
import toast from "react-hot-toast";
import {
  CheckCircleIcon,
  MinusCircleIcon,
  FunnelIcon,
} from "@heroicons/react/24/outline";

const FIELD_LABELS = {
  first_name: "First Name",
  last_name: "Last Name",
  name: "Full Name",
  number: "Jersey Number",
  position: "Position",
  grade: "Grade",
  grad_year: "Grad Year",
  dob: "Date of Birth",
  email: "Email",
  parent_emails: "Parent Emails",
  notes: "Notes",
  group: "Group/Team",
};

export default function PlayerImport({
  evaluationId,
  onComplete,
  onCancel,
  featureFlags,
}) {
  const [parsedPlayers, setParsedPlayers] = useState([]);
  const [errors, setErrors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedPlatform, setSelectedPlatform] = useState("auto");
  const [columnMapping, setColumnMapping] = useState(null);
  const [detectedPlatform, setDetectedPlatform] = useState(null);
  const [filteredRowCount, setFilteredRowCount] = useState(0);
  const csvTextRef = useRef(null);

  const doParse = (csvText, platformId) => {
    const result = parseCSV(csvText, evaluationId, platformId);
    setParsedPlayers(result.players);
    setErrors(result.errors);
    setColumnMapping(result.columnMapping);
    setDetectedPlatform(result.detectedPlatform);
    setFilteredRowCount(result.filteredRowCount || 0);
  };

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        const csv = e.target.result;
        csvTextRef.current = csv;
        doParse(csv, effectivePlatform);
      };
      reader.readAsText(file);
    }
  };

  const handlePlatformChange = (platformId) => {
    setSelectedPlatform(platformId);
    if (csvTextRef.current) {
      doParse(csvTextRef.current, platformId);
    }
  };

  const handleImport = async () => {
    if (errors.length > 0) {
      toast.error("Please fix all errors before importing");
      return;
    }

    setLoading(true);
    try {
      const playersWithEvaluation = parsedPlayers.map((player) => ({
        ...player,
        evaluation_id: evaluationId,
        created_at: new Date().toISOString(),
      }));

      const imported = await apiService.bulkCreatePlayers(
        playersWithEvaluation,
      );
      toast.success(`Successfully imported ${imported.length} players`);
      onComplete(imported);
    } catch (error) {
      console.error("Error importing players:", error);
      toast.error(
        error.message || "Failed to import players. Please try again.",
      );
    }
    setLoading(false);
  };

  // null featureFlags means not yet loaded or no plan set — default to all allowed
  const allowThirdPartyImport =
    !featureFlags ||
    featureFlags.allow_teamsnap_import ||
    featureFlags.allow_sportsengine_import;

  // When restricted to generic, force the effective platform regardless of state
  const effectivePlatform = !allowThirdPartyImport
    ? "generic"
    : selectedPlatform;

  const profiles = allowThirdPartyImport
    ? getAllProfiles()
    : getAllProfiles().filter((p) => p.id === "generic");

  const activeProfile = detectedPlatform
    ? getProfileById(detectedPlatform)
    : null;

  // Count how many fields were mapped
  const mappedCount = columnMapping
    ? Object.values(columnMapping).filter((v) => v !== null).length
    : 0;

  return (
    <div className="space-y-4">
      <div>
        <p className="text-sm text-gray-600 mb-4">
          Upload a CSV roster export from your team management platform, or a
          generic CSV file.
        </p>
      </div>

      {/* Platform Selector */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Import Source
        </label>
        <Select
          value={effectivePlatform}
          onValueChange={
            allowThirdPartyImport ? handlePlatformChange : undefined
          }
          disabled={!allowThirdPartyImport}
          className="max-w-xs"
        >
          {allowThirdPartyImport && <option value="auto">Auto-detect</option>}
          {profiles.map((p) => (
            <option key={p.id} value={p.id}>
              {p.label}
            </option>
          ))}
        </Select>
        {!allowThirdPartyImport && (
          <p className="text-xs text-gray-500 mt-1">
            Upgrade your plan to import from TeamSnap, SportsEngine, and more.
          </p>
        )}
        {allowThirdPartyImport && effectivePlatform !== "auto" && (
          <p className="text-xs text-gray-500 mt-1">
            {getProfileById(effectivePlatform).description}
          </p>
        )}
      </div>

      {/* Format Example */}
      {(!columnMapping || effectivePlatform === "generic") && (
        <div className="bg-slate-50 border border-slate-200 rounded-md p-3">
          <h4 className="text-sm font-medium text-slate-900 mb-2">
            CSV Format Example:
          </h4>
          <code className="text-xs text-slate-800 block">
            First_Name,Last_Name,Number,Position,Grade,Grad_Year,DOB,Email,Notes,Group
            <br />
            John,Smith,23,Midfield,11,2025,2006-05-15,john@email.com,Strong
            player,Team A
          </code>
          {allowThirdPartyImport && (
            <p className="text-xs text-slate-700 mt-2">
              <strong>Tip:</strong> You can also upload a roster export directly
              from TeamSnap, SportsEngine, Crossbar, or LeagueApps.
            </p>
          )}
        </div>
      )}

      <Input
        type="file"
        accept=".csv"
        onChange={handleFileUpload}
        label="Choose CSV File"
      />

      {/* Auto-detect Banner */}
      {selectedPlatform === "auto" &&
        detectedPlatform &&
        detectedPlatform !== "generic" &&
        columnMapping && (
          <div className="bg-slate-50 border border-slate-200 rounded-md px-3 py-2 flex items-center gap-2">
            <CheckCircleIcon className="w-4 h-4 text-slate-600 flex-shrink-0" />
            <span className="text-sm text-slate-800">
              Detected format:{" "}
              <span className="font-medium">{activeProfile?.label}</span>
            </span>
          </div>
        )}

      {/* Filtered Rows Notice */}
      {filteredRowCount > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-md px-3 py-2 flex items-center gap-2">
          <FunnelIcon className="w-4 h-4 text-amber-600 flex-shrink-0" />
          <span className="text-sm text-amber-800">
            {filteredRowCount} non-player/staff{" "}
            {filteredRowCount === 1 ? "row was" : "rows were"} excluded from the
            import.
          </span>
        </div>
      )}

      {/* Column Mapping Preview */}
      {columnMapping && (
        <div className="bg-gray-50 border border-gray-200 rounded-md p-3">
          <h4 className="text-sm font-medium text-gray-700 mb-2">
            Column Mapping ({mappedCount} of {Object.keys(FIELD_LABELS).length}{" "}
            fields matched)
          </h4>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-1">
            {Object.entries(FIELD_LABELS).map(([field, label]) => {
              const mapped = columnMapping[field];
              return (
                <div key={field} className="flex items-center gap-1.5 text-xs">
                  {mapped ? (
                    <CheckCircleIcon className="w-3.5 h-3.5 text-green-500 flex-shrink-0" />
                  ) : (
                    <MinusCircleIcon className="w-3.5 h-3.5 text-gray-300 flex-shrink-0" />
                  )}
                  <span className={mapped ? "text-gray-700" : "text-gray-400"}>
                    {label}
                  </span>
                  {mapped && (
                    <span className="text-gray-400 truncate">
                      &larr; {mapped.matchedHeader}
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Errors */}
      {errors.length > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <h4 className="text-sm font-medium text-red-900 mb-2">
            Errors found:
          </h4>
          <ul className="text-sm text-red-800 space-y-1">
            {errors.map((error, index) => (
              <li key={index}>&bull; {error}</li>
            ))}
          </ul>
        </div>
      )}

      {/* Player Preview */}
      {parsedPlayers.length > 0 && (
        <div className="bg-gray-50 border border-gray-200 rounded-md p-4">
          <h4 className="text-sm font-medium text-gray-900 mb-3">
            Preview ({parsedPlayers.length} players)
          </h4>
          <div className="space-y-2 max-h-48 overflow-y-auto">
            {parsedPlayers.slice(0, 10).map((player, index) => (
              <div
                key={index}
                className="text-sm text-gray-700 bg-white p-2 rounded border"
              >
                <span className="font-medium">
                  {player.first_name} {player.last_name}
                </span>
                {player.number && (
                  <span className="text-gray-500"> - #{player.number}</span>
                )}
                {player.position && (
                  <span className="text-gray-500"> - {player.position}</span>
                )}
                {player.grade && (
                  <span className="text-gray-500"> - Grade {player.grade}</span>
                )}
                {player.group && (
                  <span className="text-gray-500"> ({player.group})</span>
                )}
              </div>
            ))}
            {parsedPlayers.length > 10 && (
              <div className="text-sm text-gray-500 italic">
                ... and {parsedPlayers.length - 10} more players
              </div>
            )}
          </div>
        </div>
      )}

      <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
        <Button variant="secondary" onClick={onCancel}>
          Cancel
        </Button>
        <Button
          onClick={handleImport}
          disabled={parsedPlayers.length === 0 || loading || errors.length > 0}
        >
          {loading ? "Importing..." : `Import ${parsedPlayers.length} Players`}
        </Button>
      </div>
    </div>
  );
}
